import { ref, computed } from "vue";

const STORAGE_KEY = "mobile_inventory_assignments";

const assignmentHistory = ref([]);

export default function useMobileAssignmentStorage() {
  const loadAssignments = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        assignmentHistory.value = JSON.parse(saved);
      }
    } catch (error) {
      console.error("Error loading assignments:", error);
      assignmentHistory.value = [];
    }
  };

  const saveAssignments = () => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(assignmentHistory.value)
      );
    } catch (error) {
      console.error("Error saving assignments:", error);
    }
  };

  const addAssignment = (deviceData, unitData) => {
    const assignment = {
      id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      deviceId: deviceData.id,
      deviceName: deviceData.name || deviceData.externalId,
      deviceExternalId: deviceData.externalId,
      unitId: unitData.id,
      unitName: unitData.name,
      timestamp: new Date().toISOString(),
      status: "completed",
    };

    assignmentHistory.value.push(assignment);
    saveAssignments();

    return assignment;
  };

  const removeAssignment = (assignmentId) => {
    const index = assignmentHistory.value.findIndex(
      (a) => a.id === assignmentId
    );
    if (index > -1) {
      assignmentHistory.value.splice(index, 1);
      saveAssignments();
      return true;
    }
    return false;
  };

  const clearAssignments = () => {
    assignmentHistory.value = [];
    localStorage.removeItem(STORAGE_KEY);
  };

  const getAssignmentSummary = computed(() => {
    return {
      totalAssignments: assignmentHistory.value.length,
      completedAssignments: assignmentHistory.value.filter(
        (a) => a.status === "completed"
      ).length,
      lastAssignment:
        assignmentHistory.value[assignmentHistory.value.length - 1] || null,
      assignmentsByUnit: assignmentHistory.value.reduce((acc, assignment) => {
        const unitName = assignment.unitName;
        if (!acc[unitName]) {
          acc[unitName] = [];
        }
        acc[unitName].push(assignment);
        return acc;
      }, {}),
    };
  });

  const isDeviceAssigned = (deviceId) => {
    return assignmentHistory.value.some((a) => a.deviceId === deviceId);
  };

  const generateAssignmentReport = () => {
    if (assignmentHistory.value.length === 0) {
      return "No devices were assigned in this session.";
    }

    let report = `Device Assignment Report\n`;
    report += `Generated: ${new Date().toLocaleString()}\n`;
    report += `Total Assignments: ${assignmentHistory.value.length}\n\n`;

    const byUnit = getAssignmentSummary.value.assignmentsByUnit;

    Object.entries(byUnit).forEach(([unitName, assignments]) => {
      report += `Unit: ${unitName}\n`;
      assignments.forEach((assignment) => {
        report += `  - ${assignment.deviceName} (${assignment.deviceExternalId})\n`;
      });
      report += "\n";
    });

    return report;
  };

  loadAssignments();

  return {
    assignmentHistory: computed(() => assignmentHistory.value),
    assignmentSummary: getAssignmentSummary,

    addAssignment,
    removeAssignment,
    clearAssignments,
    loadAssignments,
    saveAssignments,
    isDeviceAssigned,
    generateAssignmentReport,
  };
}
